import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, CheckCircle2, Phone, FileText, CreditCard } from "lucide-react";
import { API, api } from "@/lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString()}`;

export default function InvoicePublic() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payMsg, setPayMsg] = useState(null);

  const loadInvoice = () => api.get(`/public/invoices/${id}`).then(({ data }) => setData(data)).catch(() => setErr(true));

  async function pollStatus(sid, attempt) {
    if (attempt === 0) setPayMsg({ type: "pending", text: "Confirming your payment…" });
    if (attempt >= 8) { setPayMsg({ type: "pending", text: "Still processing — check your email for confirmation." }); return; }
    try {
      const { data: st } = await api.get(`/checkout/status/${sid}`);
      if (st.payment_status === "paid") { setPayMsg({ type: "success", text: "Payment received — thank you! 🎉" }); loadInvoice(); return; }
      if (st.status === "expired") { setPayMsg({ type: "error", text: "That payment session expired. Please try again." }); return; }
    } catch { /* keep polling */ }
    setTimeout(() => pollStatus(sid, attempt + 1), 2000);
  }

  useEffect(() => {
    loadInvoice();
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (sid) pollStatus(sid, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const payNow = async () => {
    setPaying(true);
    try {
      const { data: res } = await api.post(`/invoices/${id}/checkout`, { origin_url: window.location.origin });
      window.location.href = res.url;
    } catch {
      setPaying(false);
      setPayMsg({ type: "error", text: "Could not start checkout. Please try again or give us a call." });
    }
  };

  if (err) return <div className="min-h-screen grid place-items-center bg-cream font-body text-charcoal">Invoice not found.</div>;
  if (!data) return <div className="min-h-screen grid place-items-center bg-cream"><Loader2 className="w-8 h-8 animate-spin text-coral" /></div>;

  const inv = data.invoice;
  const isDeposit = inv.amount < data.quote_total;

  return (
    <div className="min-h-screen bg-cream py-6 md:py-12 px-4" data-testid="invoice-public">
      <Helmet><title>Your Skedaddle Invoice</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <div className="mx-auto max-w-2xl bg-white border-2 border-charcoal rounded-[2rem] shadow-brutlg overflow-hidden">
        {/* header */}
        <div className="bg-charcoal text-cream px-6 md:px-10 py-7">
          <div className="flex items-center justify-between">
            <div className="font-heading font-bold text-2xl">Skedaddle <span className="text-coral">Junk Removal</span></div>
            <FileText className="w-8 h-8 text-sunny" />
          </div>
          <p className="font-body text-cream/70 mt-1 text-sm">Invoice · Serving the Lowcountry, SC · 843-518-0021</p>
        </div>

        <div className="px-6 md:px-10 py-8">
          <p className="font-body text-charcoal/60">Billed to</p>
          <h1 className="font-heading font-bold text-3xl">{data.customer_name || "Valued customer"}</h1>
          {data.approved && (
            <div className="mt-3 inline-flex items-center gap-2 bg-green-500 text-cream font-body font-bold text-sm rounded-full px-3 py-1">
              <CheckCircle2 className="w-4 h-4" /> Quote approved
            </div>
          )}

          {/* amount due */}
          <div className="mt-6 bg-cream border-2 border-charcoal rounded-2xl p-6 text-center">
            <p className="font-body text-charcoal/60 text-sm">{inv.description || (isDeposit ? "Deposit" : "Amount due")}</p>
            <p className="font-heading font-bold text-5xl text-coral mt-1" data-testid="invoice-amount">{money(inv.amount)}</p>
            {isDeposit && data.quote_total > 0 && (
              <p className="font-body text-xs text-charcoal/50 mt-2">Part of a {money(data.quote_total)} job</p>
            )}
          </div>

          {/* quote breakdown */}
          {data.line_items?.length > 0 && (
            <div className="mt-6">
              <p className="font-heading font-bold text-charcoal/70 mb-2">From your approved quote</p>
              <div className="border-2 border-charcoal rounded-2xl overflow-hidden">
                {data.line_items.map((li, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b border-charcoal/10 last:border-0 font-body text-sm">
                    <span>{li.label}</span><span className="font-bold">{money(li.amount)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between px-4 py-3 bg-charcoal text-cream font-heading font-bold">
                  <span>Job total</span><span>{money(data.quote_total)}</span>
                </div>
              </div>
            </div>
          )}

          {/* running balance */}
          {(data.amount_paid > 0 || data.quote_total > 0) && (
            <div className="mt-6 grid grid-cols-3 gap-2 text-center">
              <div className="bg-cream border-2 border-charcoal rounded-xl py-3"><div className="text-xs font-body text-charcoal/60">Job total</div><div className="font-heading font-bold">{money(data.quote_total)}</div></div>
              <div className="bg-teal text-cream border-2 border-charcoal rounded-xl py-3"><div className="text-xs font-body opacity-80">Paid</div><div className="font-heading font-bold">{money(data.amount_paid)}</div></div>
              <div className={`border-2 border-charcoal rounded-xl py-3 ${data.balance <= 0 && data.quote_total > 0 ? "bg-green-500 text-cream" : "bg-coral text-cream"}`}><div className="text-xs font-body opacity-80">{data.balance < 0 ? "Overpaid" : "Balance"}</div><div className="font-heading font-bold" data-testid="invoice-balance">{money(Math.abs(data.balance))}</div></div>
            </div>
          )}

          {/* photos */}
          {inv.photo_count > 0 && (
            <div className="mt-6 flex gap-3 flex-wrap">
              {Array.from({ length: inv.photo_count }).map((_, i) => (
                <img key={i} src={`${API}/public/invoices/${id}/photo/${i}`} alt="job" className="w-24 h-24 rounded-xl object-cover border-2 border-charcoal" />
              ))}
            </div>
          )}

          {payMsg && (
            <div data-testid="pay-status" className={`mt-6 rounded-2xl border-2 border-charcoal px-4 py-3 font-body font-bold text-center ${payMsg.type === "success" ? "bg-green-500 text-cream" : payMsg.type === "error" ? "bg-coral text-cream" : "bg-sunny text-charcoal"}`}>
              {payMsg.type === "pending" && <Loader2 className="w-4 h-4 inline animate-spin mr-2" />}{payMsg.text}
            </div>
          )}

          {/* pay info */}
          <div className="mt-8 bg-sunny border-2 border-charcoal rounded-2xl p-5">
            <p className="font-heading font-bold text-lg">How to pay</p>
            {data.balance > 0 ? (
              <>
                <p className="font-body text-charcoal/80 text-sm mt-1">Pay securely by card now, or use cash, Zelle, Venmo, Cash App or check.</p>
                <button onClick={payNow} disabled={paying} data-testid="pay-now-btn"
                  className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-coral text-cream font-heading font-bold rounded-full px-7 py-3.5 border-2 border-charcoal shadow-brut hover:scale-[1.02] transition-transform disabled:opacity-60">
                  {paying ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CreditCard className="w-5 h-5" /> Pay {money(inv.amount)} now with card</>}
                </button>
                <div className="mt-3">
                  <a href="tel:+18435180021" className="inline-flex items-center gap-2 font-body font-bold text-charcoal/70 text-sm"><Phone className="w-4 h-4" /> Prefer to talk? Call 843-518-0021</a>
                </div>
              </>
            ) : (
              <p className="font-body text-charcoal/80 text-sm mt-1">This invoice is fully paid — thank you! 🎉</p>
            )}
          </div>

          <p className="mt-6 text-center font-body text-charcoal/50 text-sm">Thanks for choosing Skedaddle. One call and it skedaddles!</p>
        </div>
      </div>
    </div>
  );
}
